// Interacción con la escena: pasar el cursor sobre pines, crear cables, mover y colocar piezas,
// y usar los controles de las piezas (botones, perillas, interruptores, deslizadores).
import * as THREE from 'three';
import { getDef, getPins, PITCH } from '../parts/index.js';
import { BB_H } from '../parts/breadboard.js';
import { partProps, toLocal, toWorld, snapPosition, rotDir } from '../connectivity.js';
import { buildWire, disposeWire, wireHex } from './wires.js';
import { buildNetlist } from '../netlist.js';
import { escapeHtml } from '../ui/html.js';

const PIN_PX = 13;
const KIND_COLORS = { power: '#ff5a4f', gnd: '#9aa4b1', analog: '#4fc3ff', pwm: '#c08bff', digital: '#5ce08a', signal: '#ffd166', hole: '#e8e8e8' };

export class Interaction {
  constructor(app) {
    this.app = app;
    this.stage = app.stage;
    this.world = app.world;
    this.canvas = this.stage.renderer.domElement;
    this.mode = 'idle';
    this.hover = { pin: null, part: null, wire: null, control: null };
    this.pending = null;
    this.wireStart = null;
    this.ghostWire = null;
    this.placing = null;
    this.control = null;
    this.lastMove = null;

    // Marcador del pin bajo el cursor (siempre visible, encima de todo)
    const ringGeo = new THREE.RingGeometry(1.05, 1.6, 28).rotateX(-Math.PI / 2);
    this.marker = new THREE.Group();
    this.markerRing = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color: '#ffffff', depthTest: false, transparent: true, opacity: 0.95 }));
    this.markerDot = new THREE.Mesh(new THREE.SphereGeometry(0.55, 14, 10), new THREE.MeshBasicMaterial({ color: '#ffffff', depthTest: false }));
    this.marker.add(this.markerRing, this.markerDot);
    this.marker.renderOrder = 20;
    this.markerRing.renderOrder = 20;
    this.markerDot.renderOrder = 21;
    this.marker.visible = false;
    this.stage.overlay.add(this.marker);
    // Marcador del pin de origen al tender un cable
    this.startMarker = this.marker.clone(true);
    this.startMarker.children.forEach((c) => (c.material = c.material.clone()));
    this.startMarker.visible = false;
    this.stage.overlay.add(this.startMarker);
    // Contorno de selección
    this.outline = new THREE.LineLoop(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: '#2fd3e3', depthTest: false, transparent: true, opacity: 0.95 }));
    this.outline.renderOrder = 19;
    this.outline.visible = false;
    this.stage.overlay.add(this.outline);
    this.hoverOutline = new THREE.LineLoop(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: '#7fb4bb', depthTest: false, transparent: true, opacity: 0.5 }));
    this.hoverOutline.renderOrder = 18;
    this.hoverOutline.visible = false;
    this.stage.overlay.add(this.hoverOutline);
    // Puntos conectados eléctricamente al pin bajo el cursor
    this.netMarkers = new THREE.InstancedMesh(
      new THREE.RingGeometry(0.75, 1.15, 20).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: '#2fd3e3', depthTest: false, transparent: true, opacity: 0.85 }),
      400,
    );
    this.netMarkers.renderOrder = 19;
    this.netMarkers.count = 0;
    this.netMarkers.frustumCulled = false;
    this.stage.overlay.add(this.netMarkers);
    app.on('layout', () => {
      this.netlist = null;
    });
    // Agujeros que ocupará la pieza que se arrastra (verde) o patas fuera de la rejilla (rojo)
    this.legMarkers = new THREE.InstancedMesh(
      new THREE.RingGeometry(0.7, 1.2, 18).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ depthTest: false, transparent: true, opacity: 0.9 }),
      120,
    );
    this.legMarkers.renderOrder = 19;
    this.legMarkers.count = 0;
    this.legMarkers.frustumCulled = false;
    this.stage.overlay.add(this.legMarkers);

    // Los oyentes van antes que los de OrbitControls para poder desactivarlos a tiempo.
    const c = this.canvas;
    c.addEventListener('pointerdown', (e) => this.onDown(e), { capture: true });
    c.addEventListener('pointermove', (e) => this.onMove(e));
    window.addEventListener('pointerup', (e) => this.onUp(e));
    c.addEventListener('pointerleave', () => this.clearHover());
    c.addEventListener('wheel', (e) => this.onWheel(e), { passive: false, capture: true });
    c.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (this.mode === 'wiring' || this.mode === 'placing') this.cancel();
    });
    c.addEventListener('dblclick', (e) => this.onDblClick(e));
  }

  get project() {
    return this.app.project;
  }

  // Selección por rayo ------------------------------------------------------------------------
  hits(e) {
    return this.stage
      .raycast(e.clientX, e.clientY, [this.world.partsRoot, this.world.wiresRoot])
      .filter((h) => shown(h.object) && !h.object.userData.noPick && (!this.placing || h.object.userData.partId !== this.placing.id));
  }

  pick(e) {
    const hits = this.hits(e);
    const first = hits[0] || null;
    const ud = first ? first.object.userData : {};
    const control = ud.interactive && ud.partId ? { partId: ud.partId, ...ud.interactive, object: first.object } : null;
    let pin = this.findPin(e, first, hits);
    if (pin && control && pin.px > 6) pin = null;
    return { first, partId: ud.partId || null, wireId: ud.wireId || null, control, pin };
  }

  findPin(e, first, hits = first ? [first] : []) {
    const rect = this.canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const cam = this.stage.camera.position;
    // Una pieza no tapa sus propios pines (las patas de un LED quedan bajo su cápsula): solo cuentan
    // como obstáculo las demás piezas y los cables.
    // Los cables tampoco: su conector queda justo encima del pin.
    const occluder = (partId) => {
      for (const h of hits) if (h.object.userData.partId !== partId && !h.object.userData.wireId) return h.distance;
      return Infinity;
    };
    let best = null;
    let bestD = PIN_PX;
    const v = new THREE.Vector3();
    const s = { x: 0, y: 0, z: 0 };
    const layout = this.app.layout;
    for (const part of this.project.parts) {
      if (part._ghost) continue;
      const def = getDef(part.type);
      if (def.kind === 'breadboard') continue;
      const y = layout.y.get(part.id) || 0;
      const props = partProps(part);
      const hitDist = occluder(part.id);
      for (const p of getPins(part.type, props)) {
        const [wx, wz] = toWorld(part, p.x, p.z);
        v.set(wx, y + p.y, wz);
        this.stage.toScreen(v, s);
        if (s.z > 1) continue;
        const d = Math.hypot(s.x - mx, s.y - my);
        if (d < bestD && cam.distanceTo(v) <= hitDist + 4) {
          best = { part: part.id, pin: p.name, def: p, pos: v.clone(), dir: p.dir ? rotDir(p.dir, part.rot || 0) : [0, 1, 0], px: d };
          bestD = d;
        }
      }
    }
    // Agujeros de la protoboard bajo el cursor
    if (first && first.object.userData.partId) {
      const bb = this.project.parts.find((p) => p.id === first.object.userData.partId);
      if (bb && getDef(bb.type).kind === 'breadboard') {
        const def = getDef(bb.type);
        const props = partProps(bb);
        const [lx, lz] = toLocal(bb, first.point.x, first.point.z);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dz = -1; dz <= 1; dz++) {
            const hole = def.holeAt(props, lx + dx * PITCH, lz + dz * PITCH, PITCH * 0.55);
            if (!hole) continue;
            const hp = getPins(bb.type, props).find((p) => p.name === hole);
            const [wx, wz] = toWorld(bb, hp.x, hp.z);
            v.set(wx, BB_H, wz);
            this.stage.toScreen(v, s);
            const d = Math.hypot(s.x - mx, s.y - my);
            if (d < bestD + 4 && (!best || d < best.px)) {
              best = { part: bb.id, pin: hole, def: hp, pos: v.clone(), dir: [0, 1, 0], px: d, hole: true };
              bestD = d;
            }
          }
        }
      }
    }
    return best;
  }

  // Eventos -----------------------------------------------------------------------------------
  onDown(e) {
    if (e.button === 2) return;
    this.downAt = { x: e.clientX, y: e.clientY, t: performance.now() };
    if (e.button !== 0) return;
    if (this.mode === 'placing') {
      this.stage.controls.enabled = false;
      this.finishPlacing();
      return;
    }
    const pk = this.pick(e);
    if (this.mode === 'wiring') {
      this.stage.controls.enabled = false;
      if (pk.pin && !(pk.pin.part === this.wireStart.part && pk.pin.pin === this.wireStart.pin)) this.finishWire(pk.pin);
      else if (!pk.pin) this.cancel();
      return;
    }
    if (pk.pin && !pk.control) {
      // Clic en un pin = tender un cable; arrastrar desde ahí = mover la pieza (la protoboard está
      // cubierta de agujeros y solo así se puede arrastrar).
      this.stage.controls.enabled = false;
      const owner = pk.partId ? this.project.parts.find((p) => p.id === pk.partId) : null;
      const y = owner ? this.app.layout.y.get(owner.id) || 0 : 0;
      const gp = owner && this.stage.groundPoint(e.clientX, e.clientY, y);
      this.pending = { type: 'pin', pin: pk.pin, id: owner && owner.id, start: { x: e.clientX, y: e.clientY }, offset: gp ? { x: owner.x - gp.x, z: owner.z - gp.z } : null, y };
      return;
    }
    if (pk.control) {
      this.stage.controls.enabled = false;
      this.startControl(pk.control, e);
      return;
    }
    if (pk.partId) {
      this.stage.controls.enabled = false;
      const part = this.project.parts.find((p) => p.id === pk.partId);
      this.app.select({ type: 'part', id: pk.partId });
      if (part && !this.app.locked) {
        const y = this.app.layout.y.get(part.id) || 0;
        const gp = this.stage.groundPoint(e.clientX, e.clientY, y);
        this.pending = { type: 'drag', id: part.id, start: { x: e.clientX, y: e.clientY }, offset: gp ? { x: part.x - gp.x, z: part.z - gp.z } : { x: 0, z: 0 }, y, before: null };
      }
      return;
    }
    if (pk.wireId) {
      this.stage.controls.enabled = false;
      this.app.select({ type: 'wire', id: pk.wireId });
      return;
    }
    this.pending = { type: 'empty', start: { x: e.clientX, y: e.clientY } };
  }

  onMove(e) {
    this.lastMove = e;
    if (this.mode === 'control') return this.moveControl(e);
    if (this.pending && this.pending.type === 'pin' && Math.hypot(e.clientX - this.pending.start.x, e.clientY - this.pending.start.y) > 4) {
      if (this.pending.id && this.pending.offset && !this.app.locked) {
        this.app.select({ type: 'part', id: this.pending.id });
        this.pending.type = 'drag';
      } else this.pending = null;
    }
    if (this.pending && this.pending.type === 'drag') {
      const d = Math.hypot(e.clientX - this.pending.start.x, e.clientY - this.pending.start.y);
      if (this.mode !== 'drag' && d > 4) {
        this.mode = 'drag';
        this.app.beginChange();
        this.canvas.style.cursor = 'grabbing';
      }
      if (this.mode === 'drag') return this.moveDrag(e);
    }
    if (this.mode === 'placing') return this.movePlacing(e);
    this.updateHover(e);
    if (this.mode === 'wiring') this.updateGhostWire(e);
  }

  onUp(e) {
    if (this.mode === 'control') this.endControl(e);
    if (this.pending && this.pending.type === 'pin' && e.button === 0) {
      const pin = this.pending.pin;
      this.pending = null;
      this.startWire(pin);
      return;
    }
    if (this.mode === 'drag') {
      this.mode = 'idle';
      this.showLegs(null);
      this.app.endDrag();
      this.app.endChange('Mover pieza');
      this.canvas.style.cursor = '';
    } else if (this.pending && this.pending.type === 'empty' && e.button === 0) {
      const d = Math.hypot(e.clientX - this.pending.start.x, e.clientY - this.pending.start.y);
      if (d < 4) this.app.select(null);
    }
    this.pending = null;
    this.stage.controls.enabled = true;
  }

  onWheel(e) {
    const pk = this.pick(e);
    if (pk.control && (pk.control.kind === 'knob' || pk.control.kind === 'encoder')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const step = e.deltaY > 0 ? -1 : 1;
      if (pk.control.kind === 'encoder') this.app.partInput(pk.control.partId, 'rotate', step);
      else {
        const part = this.project.parts.find((p) => p.id === pk.control.partId);
        const v = Math.min(1, Math.max(0, (partProps(part).value ?? 0.5) + step * 0.02));
        this.app.setLiveProp(part.id, pk.control.key, v, true);
      }
    }
  }

  onDblClick(e) {
    const pk = this.pick(e);
    if (pk.partId && !pk.control && !pk.pin) this.app.focusInspector();
  }

  // Hover ------------------------------------------------------------------------------------------
  updateHover(e) {
    const pk = this.pick(e);
    const h = this.hover;
    const pinKey = pk.pin ? pk.pin.part + ':' + pk.pin.pin : null;
    if (pinKey !== (h.pin ? h.pin.part + ':' + h.pin.pin : null)) {
      h.pin = pk.pin;
      this.showPinMarker(this.marker, pk.pin);
      this.showConnected(pinKey);
      this.app.showPinTip(pk.pin, e);
    } else if (pk.pin) this.app.showPinTip(pk.pin, e);
    if (h.wire !== pk.wireId) {
      if (h.wire && !(this.app.selection && this.app.selection.id === h.wire)) this.world.setWireState(h.wire, 0);
      h.wire = pk.wireId && !pk.pin ? pk.wireId : null;
      if (h.wire && !(this.app.selection && this.app.selection.id === h.wire)) this.world.setWireState(h.wire, 1);
    }
    const partHover = !pk.pin && !pk.wireId ? pk.partId : null;
    if (partHover !== h.part) {
      h.part = partHover;
      this.drawOutline(this.hoverOutline, partHover && !(this.app.selection && this.app.selection.id === partHover) ? partHover : null);
    }
    h.control = pk.control;
    let cursor = '';
    if (this.mode === 'wiring') cursor = pk.pin ? 'crosshair' : 'default';
    else if (pk.pin) cursor = this.app.locked ? 'default' : 'crosshair';
    else if (pk.control) cursor = pk.control.kind === 'knob' ? 'ns-resize' : pk.control.kind === 'slider' ? 'ew-resize' : pk.control.kind === 'stick' ? 'move' : 'pointer';
    else if (pk.partId) cursor = this.app.locked ? 'default' : 'grab';
    else if (pk.wireId) cursor = 'pointer';
    this.canvas.style.cursor = cursor;
    this.app.setHint(this.hintFor(pk));
    this.stage.invalidate();
  }

  hintFor(pk) {
    if (this.mode === 'wiring') return '<b>Clic en otro pin</b> para terminar el cable · <b>Esc</b> o clic derecho para cancelar';
    if (this.mode === 'placing') return '<b>Clic</b> para colocar · <b>R</b> para girar · <b>Esc</b> para cancelar';
    if (pk.pin) return this.app.locked ? '' : `<b>Clic</b> para tender un cable desde ${pk.pin.hole ? 'este agujero' : 'este pin'}`;
    if (pk.control) {
      const l = { press: 'Mantén presionado', toggle: 'Clic para cambiar', knob: 'Arrastra arriba/abajo o usa la rueda', slider: 'Arrastra para mover', key: 'Clic para presionar', encoder: 'Rueda para girar · clic para presionar', stick: 'Arrastra en cualquier dirección' }[pk.control.kind];
      return `<b>${escapeHtml(pk.control.label || 'Control')}</b> · ${l || ''}`;
    }
    if (pk.partId) return this.app.locked ? '' : '<b>Arrastra</b> para mover · <b>R</b> gira · <b>Supr</b> elimina';
    if (pk.wireId) return '<b>Clic</b> para seleccionar el cable';
    return '';
  }

  clearHover() {
    if (this.mode === 'drag' || this.mode === 'control') return;
    if (this.hover.wire && !(this.app.selection && this.app.selection.id === this.hover.wire)) this.world.setWireState(this.hover.wire, 0);
    this.hover = { pin: null, part: null, wire: null, control: null };
    this.marker.visible = false;
    this.netMarkers.count = 0;
    this.hoverOutline.visible = false;
    this.app.showPinTip(null);
    this.app.setHint(this.mode === 'wiring' ? this.hintFor({}) : '');
    this.stage.invalidate();
  }

  /** Marca todos los pines y agujeros unidos al pin (cables, tiras de la protoboard, puentes internos). */
  showConnected(key) {
    const m = this.netMarkers;
    m.count = 0;
    if (key) {
      if (!this.netlist) this.netlist = buildNetlist(this.project, this.app.layout);
      const dummy = new THREE.Object3D();
      const dist = this.stage.camera.position.distanceTo(this.stage.controls.target);
      const k = Math.max(0.7, Math.min(2.5, dist / 180));
      for (const other of this.netlist.connected(key)) {
        if (other === key || m.count >= 400) continue;
        const [part, ...rest] = other.split(':');
        const pose = this.world.pinPose(this.project, { part, pin: rest.join(':') });
        if (!pose) continue;
        dummy.position.set(pose.pos[0], pose.pos[1] + 0.12, pose.pos[2]);
        dummy.scale.setScalar(k);
        dummy.updateMatrix();
        m.setMatrixAt(m.count++, dummy.matrix);
      }
      m.instanceMatrix.needsUpdate = true;
    }
    this.stage.invalidate();
  }

  showPinMarker(marker, pin) {
    if (!pin) {
      marker.visible = false;
      return;
    }
    const color = KIND_COLORS[pin.def.kind] || KIND_COLORS.signal;
    marker.children[0].material.color.set(color);
    marker.children[1].material.color.set(color);
    marker.position.copy(pin.pos);
    marker.position.y += 0.15;
    const dist = this.stage.camera.position.distanceTo(pin.pos);
    const k = Math.max(0.7, Math.min(3, dist / 160));
    marker.scale.setScalar(k);
    marker.visible = true;
  }

  drawOutline(line, partId) {
    const part = partId && this.project.parts.find((p) => p.id === partId);
    if (!part) {
      line.visible = false;
      return;
    }
    const y = (this.app.layout.y.get(part.id) || 0) + 0.35;
    const pts = this.world.footprintCorners(part).map(([x, z]) => new THREE.Vector3(x, y, z));
    line.geometry.dispose();
    line.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    line.visible = true;
  }

  refreshSelection() {
    const sel = this.app.selection;
    this.drawOutline(this.outline, sel && sel.type === 'part' ? sel.id : null);
    if (this.hover.part && sel && sel.id === this.hover.part) this.hoverOutline.visible = false;
    this.stage.invalidate();
  }

  // Cables ---------------------------------------------------------------------------------------
  startWire(pin) {
    if (this.app.blocked()) return;
    this.mode = 'wiring';
    this.wireStart = { part: pin.part, pin: pin.pin, pos: pin.pos.clone(), dir: pin.dir, kind: pin.def.kind };
    this.showPinMarker(this.startMarker, pin);
    this.app.setHint(this.hintFor({}));
  }

  updateGhostWire(e) {
    if (this.ghostWire) {
      this.stage.overlay.remove(this.ghostWire);
      disposeWire(this.ghostWire);
      this.ghostWire = null;
    }
    const start = this.wireStart;
    let end;
    let dir = [0, 1, 0];
    if (this.hover.pin) {
      end = this.hover.pin.pos;
      dir = this.hover.pin.dir;
    } else {
      end = this.stage.groundPoint(e.clientX, e.clientY, 0);
      if (!end) return;
      end.y = 0.5;
    }
    this.ghostWire = buildWire(start.pos.toArray(), start.dir, end.toArray(), dir, this.app.wireColorFor(start, this.hover.pin));
    this.ghostWire.traverse((o) => {
      o.userData.noPick = true;
      if (o.material) {
        o.material = o.material.clone();
        o.material.transparent = true;
        o.material.opacity = 0.75;
      }
    });
    this.stage.overlay.add(this.ghostWire);
    this.stage.invalidate();
  }

  finishWire(pin) {
    const a = this.wireStart;
    this.app.addWire({ part: a.part, pin: a.pin }, { part: pin.part, pin: pin.pin }, this.app.wireColorFor(a, pin));
    this.endWiring();
  }

  endWiring() {
    this.mode = 'idle';
    this.wireStart = null;
    this.startMarker.visible = false;
    if (this.ghostWire) {
      this.stage.overlay.remove(this.ghostWire);
      disposeWire(this.ghostWire);
      this.ghostWire = null;
    }
    this.app.setHint('');
    this.stage.invalidate();
  }

  // Colocación desde el catálogo -------------------------------------------------------------
  startPlacing(type, props = {}) {
    if (this.app.blocked()) return;
    this.cancel();
    const part = this.app.addGhost(type, props);
    this.placing = part;
    this.mode = 'placing';
    this.app.setHint(this.hintFor({}));
    const e = this.lastMove;
    if (e) this.movePlacing(e);
    else {
      const t = this.stage.controls.target;
      this.app.moveGhost(snapPosition(this.project, part, t.x, t.z));
    }
  }

  movePlacing(e) {
    const gp = this.stage.groundPoint(e.clientX, e.clientY, 0);
    if (!gp || !this.placing) return;
    this.app.moveGhost(snapPosition(this.project, this.placing, gp.x, gp.z));
    this.showLegs(this.placing);
    this.canvas.style.cursor = 'copy';
  }

  /** Marca dónde caen las patas de una pieza insertable que está sobre una protoboard. */
  showLegs(part) {
    const m = this.legMarkers;
    m.count = 0;
    const layout = this.app.layout;
    if (part && getDef(part.type).insertable && (layout.y.get(part.id) || 0) > 0) {
      const ok = layout.inserted.has(part.id);
      m.material.color.set(ok ? '#3ddc84' : '#ff5a4f');
      const dummy = new THREE.Object3D();
      for (const p of getPins(part.type, partProps(part))) {
        if (!p.insert || m.count >= 120) continue;
        const [wx, wz] = toWorld(part, p.x, p.z);
        dummy.position.set(wx, BB_H + 0.15, wz);
        dummy.updateMatrix();
        m.setMatrixAt(m.count++, dummy.matrix);
      }
      m.instanceMatrix.needsUpdate = true;
    }
    this.stage.invalidate();
  }

  finishPlacing() {
    const part = this.placing;
    this.placing = null;
    this.showLegs(null);
    this.mode = 'idle';
    this.app.placeGhost(part);
    this.app.setHint('');
  }

  rotatePlacing(dir = 1) {
    if (!this.placing) return;
    this.placing.rot = (((this.placing.rot || 0) + 90 * dir) % 360 + 360) % 360;
    if (this.lastMove) this.movePlacing(this.lastMove);
  }

  // Arrastre ---------------------------------------------------------------------------------------
  moveDrag(e) {
    const p = this.pending;
    const part = this.project.parts.find((x) => x.id === p.id);
    if (!part) return;
    const gp = this.stage.groundPoint(e.clientX, e.clientY, p.y);
    if (!gp) return;
    const pos = snapPosition(this.project, part, gp.x + p.offset.x, gp.z + p.offset.z);
    this.app.movePartLive(part.id, pos.x, pos.z);
    this.drawOutline(this.outline, part.id);
    this.showLegs(part);
  }

  // Controles de piezas ----------------------------------------------------------------------
  startControl(control, e) {
    const part = this.project.parts.find((p) => p.id === control.partId);
    if (!part) return;
    this.mode = 'control';
    this.control = { ...control, start: { x: e.clientX, y: e.clientY }, startValue: partProps(part)[control.key] };
    const view = this.world.views.get(part.id);
    switch (control.kind) {
      case 'press':
      case 'key':
        if (view && view.view.press) view.view.press(control.key, true, control.value);
        this.app.partInput(part.id, control.key, control.kind === 'key' ? { key: control.value, down: true } : true);
        this.stage.invalidate();
        break;
      case 'toggle':
        this.app.togglePart(part.id, control.key);
        break;
      case 'encoder':
        this.app.partInput(part.id, 'press', true);
        break;
      case 'knob':
      case 'slider':
        this.app.beginChange();
        break;
      case 'stick':
        // Palanca de dos ejes (joystick): vuelve al centro al soltar; un clic sin arrastrar manda clickKey.
        this.control.moved = false;
        break;
    }
  }

  moveControl(e) {
    const c = this.control;
    if (!c) return;
    if (c.kind === 'stick') {
      if (!c.moved && Math.hypot(e.clientX - c.start.x, e.clientY - c.start.y) < 4) return;
      c.moved = true;
      let x = (e.clientX - c.start.x) / 70;
      let y = (e.clientY - c.start.y) / 70;
      const r = Math.hypot(x, y);
      if (r > 1) {
        x /= r;
        y /= r;
      }
      this.app.setLiveProp(c.partId, c.keyX || 'x', Math.round(x * 1000) / 1000, false);
      this.app.setLiveProp(c.partId, c.keyY || 'y', Math.round(-y * 1000) / 1000, false);
      return;
    }
    if (c.kind === 'knob') {
      const dy = c.start.y - e.clientY;
      const dx = e.clientX - c.start.x;
      const v = Math.min(1, Math.max(0, (c.startValue ?? 0.5) + (dy + dx * 0.5) / 220));
      this.app.setLiveProp(c.partId, c.key, v, false);
    } else if (c.kind === 'slider') {
      const part = this.project.parts.find((p) => p.id === c.partId);
      const y = (this.app.layout.y.get(part.id) || 0) + 12;
      const gp = this.stage.groundPoint(e.clientX, e.clientY, y);
      if (!gp) return;
      const [lx] = toLocal(part, gp.x, gp.z);
      const v = Math.min(1, Math.max(0, lx / (c.range || 46) + 0.5));
      this.app.setLiveProp(c.partId, c.key, v, false);
    }
  }

  endControl() {
    const c = this.control;
    this.mode = 'idle';
    this.control = null;
    if (!c) return;
    const view = this.world.views.get(c.partId);
    if (c.kind === 'press' || c.kind === 'key') {
      if (view && view.view.press) view.view.press(c.key, false, c.value);
      this.app.partInput(c.partId, c.key, c.kind === 'key' ? { key: c.value, down: false } : false);
      this.stage.invalidate();
    } else if (c.kind === 'encoder') {
      this.app.partInput(c.partId, 'press', false);
    } else if (c.kind === 'stick') {
      if (c.moved) {
        this.app.setLiveProp(c.partId, c.keyX || 'x', 0, false);
        this.app.setLiveProp(c.partId, c.keyY || 'y', 0, false);
      } else if (c.clickKey) this.app.partInput(c.partId, c.clickKey, true);
    } else if (c.kind === 'knob' || c.kind === 'slider') {
      this.app.endChange('Ajustar control');
    }
  }

  cancel() {
    if (this.mode === 'wiring') this.endWiring();
    if (this.mode === 'placing' && this.placing) {
      this.app.removeGhost(this.placing);
      this.placing = null;
      this.showLegs(null);
    }
    this.mode = 'idle';
    this.app.setHint('');
    this.stage.controls.enabled = true;
  }
}

export { KIND_COLORS, wireHex };

/** Visible él y todos sus ancestros (el rayo de three.js no mira `visible`). */
function shown(obj) {
  for (let o = obj; o; o = o.parent) if (!o.visible) return false;
  return true;
}
